import { createFileRoute, redirect } from "@tanstack/react-router";
import { getQuestionsOptions } from "../../../../../../../../features/add-insurance/get-questions/queryOptions";
import { Form } from "../../../../../../../../features/form/base-form/base-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { service } from "@maia/cart";
import { mapToAnswer } from "../../../../../../../../features/form/utils/map-to-answers";
import { useCallback } from "react";
import { flow } from "es-toolkit";
import { match } from "ts-pattern";
import { twMerge } from "tailwind-merge";
import { Button } from "@maia/ui";
import * as v from "valibot";
import { getCompositionQuestionsOptions } from "apps/broker-studio/src/features/add-insurance/get-composition-questions/queryOptions";

export const Route = createFileRoute(
  "/_authenticated/add-insurance/$customer/_layout-cart/cart/$cart/product/$product/_layout-cart/",
)({
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({
    context: { queryClient },
    params: { customer, cart, product },
    deps: search,
  }) => {
    const questions = await queryClient.ensureQueryData(
      getQuestionsOptions({
        cartGuid: cart,
        productGuid: product,
        customerGuid: customer,
      }),
    );

    if (!questions.length) {
      throw redirect({
        to: "/add-insurance/$customer/cart/$cart/product/$product/compositions",
        params: { customer, cart, product },
        search,
      });
    }

    return questions;
  },
  validateSearch: v.object({
    products: v.optional(v.string()),
  }),
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const { products } = Route.useSearch();
  const { customer, cart, product } = Route.useParams();
  const queryClient = useQueryClient();

  const {
    data: questions,
    refetch,
    isRefetching,
  } = useQuery({
    ...getQuestionsOptions({
      cartGuid: cart,
      productGuid: product,
      customerGuid: customer,
    }),
    initialData: data,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (body: {
      questions: Parameters<typeof service.putQuestions>[number]["questions"];
      trigger: "revalidate" | "submit";
    }) =>
      service.putQuestions({
        cartGuid: cart,
        productGuid: product,
        customerGuid: customer,
        questions: body.questions,
      }),
    onSuccess: (_, request) =>
      match(request.trigger)
        .with('revalidate', () => refetch())
        .with('submit', async () => {
          await queryClient.invalidateQueries({
            queryKey: ['getCompositionQuestions', cart, product, customer],
          });
          await queryClient.invalidateQueries({
            queryKey: ['products'],
          });
          await refetch();
          return navigate({
            to: "/add-insurance/$customer/cart/$cart/product/$product/compositions",
            params: { customer, cart, product },
            search: {
              products,
            },
          });
        })
        .exhaustive(),
  });

  const handleRevalidate = useCallback(
    flow(mapToAnswer, (questions) => mutate({ questions, trigger: "revalidate" })),
    [mutate],
  );
  const handleSubmit = useCallback(
    flow(mapToAnswer, (questions) => mutate({ questions, trigger: "submit" })),
    [mutate],
  );

  return (
    <Form
      className={twMerge(
        (isRefetching || isPending) &&
          "pointer-events-none opacity-25 transition-opacity duration-200",
        "flex flex-col gap-y-32",
      )}
      questions={questions}
      onSubmit={handleSubmit}
      onRevalidate={handleRevalidate}
      submitButtonSlot={
        <Button intent="primary" className="w-min ml-auto" type="submit">
          Volgende
        </Button>
      }
    />
  );
}
